import type {
  DBServerRecord,
  Configuration,
  QueryDBRestoreDestination,
} from '@repo/api-gw-sdk';
import {
  GetDatabasesByServerApi,
  GetSnapshotsByServerApi,
  DatabaseSnapshotsApi,
  retry,
} from '@repo/api-gw-sdk';
import type { SWRConfiguration } from 'swr';

import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import type { HttpClient } from './httpClient';

const baseUrl = '/db';

export const db = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const snapshotsApi = new GetSnapshotsByServerApi(config);
  const getDbApi = new GetDatabasesByServerApi(config);
  const databaseSnapshotsApi = new DatabaseSnapshotsApi(config);

  return {
    // swrConfig is optional so a caller can override the global onError/retry
    // (swrDefaultConfig reports every error and auto-retries) when a fetch
    // failure is expected and handled locally — e.g. zero-etl's useResourceTables
    // tolerates TABLE_NOT_FOUND before the resource's DB snapshot is indexed.
    useSnapshots: (
      serverId: string,
      startDate: Date,
      endDate: Date,
      swrConfig?: SWRConfiguration
    ) =>
      httpClient.executeWithConfig(
        snapshotsApi.getSnapshotsByServer.bind(snapshotsApi),
        // Skip the SWR fetch when no serverId — the backend route resolves
        // `/db//snapshots` to "no matching operation" (500). Callers that
        // gate on the resource being picked rely on this to no-op until a
        // serverId is available.
        serverId ? baseUrl : null,
        '',
        swrConfig ?? {},
        currentProjectId,
        serverId,
        startDate,
        endDate
      ),
    useObjectTreeMultiple: (serverIds: string[], snapshotId: string) => {
      return httpClient.executeWithConfig(
        (projectId: string, serverIds: string[], snapshotId: string) =>
          Promise.all(
            serverIds.map((serverId) =>
              getDbApi
                .getDatabasesByServer(projectId, serverId, snapshotId)
                .catch((e) => {
                  if (e?.code !== 404) {
                    throw e;
                  }
                  return undefined;
                })
            )
          ).then((results) => {
            const distinctResults: DBServerRecord[] = [];
            for (const y of results) {
              if (
                !!y &&
                !distinctResults.some((x) => x.serverId === y.serverId)
              ) {
                distinctResults.push(y);
              }
            }

            return distinctResults;
          }),
        snapshotId ? baseUrl : null,
        '',
        {
          // A viewer without `read:db` gets a 403
          // here when opening a backup job's DB detail view. The error still
          // lands in the returned SWR state for the caller's own handling —
          // just don't forward an expected permission denial to Sentry.
          onError: (e) => {
            if ((e?.code ?? e?.status) !== 403) {
              window.reportError(e);
            }
          },
        },
        currentProjectId,
        serverIds,
        snapshotId
      );
    },
    querySnapshot: (
      snapshotId: string,
      databaseName: string,
      query: string,
      targetAccountId: string,
      destination: QueryDBRestoreDestination,
      signal?: AbortSignal
    ) => {
      if (!snapshotId) {
        return Promise.resolve(undefined);
      }

      return retry(
        () =>
          databaseSnapshotsApi.runQuery(
            currentProjectId,
            snapshotId,
            {
              restoreAccountId: targetAccountId,
              databaseName,
              query,
              destination,
            },
            {
              signal,
            }
          ),
        3 // Picked by Peleg as his lucky number
      );
    },
    queryStatus: (queryId: string) =>
      databaseSnapshotsApi.getQueryStatus(currentProjectId, queryId),
    queryResultsPromise: (queryId: string, isSupported: boolean) =>
      isSupported
        ? databaseSnapshotsApi.getQueryResult(currentProjectId, queryId)
        : Promise.resolve({
            columns: [],
            records: [],
            nextToken: null,
          }),
    useQueryResults: (queryId: string) => {
      const { RecordRestoreResultsEnabled } = useFeatureFlags();
      return httpClient.execute(
        (projectId: string, queryId: string) =>
          RecordRestoreResultsEnabled
            ? databaseSnapshotsApi.getQueryResult(projectId, queryId)
            : Promise.resolve({
                columns: [],
                records: [],
                nextToken: null,
              }),
        baseUrl,
        '',
        currentProjectId,
        queryId
      );
    },
    generateQuery: (text: string) =>
      httpClient.post('/projects/{projectId}/db/generate-query', { text }),
  };
};
